<script setup lang="ts">
// Vue
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

// Providers
import BuildingTypes, { useBuildingTypes } from "@/dataProviders/BuildingTypes";
import CalculationTypes, { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import DefaultModels, { useDefaultModels } from "@/dataProviders/DefaultModels";
import { useCreateBuildingProject, useModels, type LocalModelType } from "@/features/BuildingProject";
import { buildingTypeDisplayLabel, isIndentedBuildingType } from "@/features/BuildingProject/utils/buildingTypeLabel";
import {
  calculationTypeDefaults,
  type SelectableCalculationType,
} from "@/features/BuildingProject/utils/calculationTypeDefaults";
import { commonCalculationTypeLabelFor } from "@/features/BuildingProject/utils/calculationTypeCopy";
import { suppressThresholdFallback } from "@/features/BuildingProject/utils/thresholdFallback";
import { canStartProject } from "@/pages/Projects/utils/canStartProject";
import { PROJECT_NAME_TAKEN_MESSAGE, useProjectNameAvailability } from "@/composables/useProjectNameAvailability.composable";
import { ProjectSetupIncompleteError } from "@/features/BuildingProject/services/projectWriteErrors";
import { countryLongName, creatableCountries } from "@/providers/Localization";
import { useIndicators, type IndicatorTypeType, type DenominatorUnitType } from "@/dataProviders/Indicators";
import type { CalculationType, Country_Enum } from "@/graphql";

// Enums
import { BuildingProjectStageEnums } from "@/enums";

// Stores
import { useModalStore } from "@/stores/modals/modals.store";
import { useWelcomeFlowStore } from "@/stores/welcomeFlow/welcomeFlow.store";
import { isProjectTourPending } from "@features/WelcomeFlow";

// Services
import { services as BuildingProjectServices } from "@/features/BuildingProject";

// Utils
import { isAxiosError } from "axios";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

// Components
import { ArrowRight, Building2 } from "@lucide/vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import YearInput from "@/components/Reusables/Inputs/YearInput.vue";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import LifecycleStageStrip from "@/features/BuildingProject/components/LifecycleStageStrip.vue";
import ModelSelect from "@/features/BuildingProject/components/ModelSelect/ModelSelect.vue";

/** The four required answers — everything else moved to the settings page. Custom calculation types are the one exception, needing three more (see needsCalculationDetails). */
const { closeByName } = useModalStore();
const calculationTypes = useCalculationTypes();
const buildingTypes = useBuildingTypes();
const createBuildingProject = useCreateBuildingProject();
const models = useModels();
const defaultModels = useDefaultModels();
const router = useRouter();
const toast = useToast();

const isCreatingProject = ref<boolean>(false);

const selectedCountry = ref<Country_Enum | null>(null);
const projectName = ref<string>("");
const buildingTypeId = ref<number | null>(null);
const calculationTypeId = ref<string | number | null>(null);

/** `string | number`: SelectInputWithKeyValue emits the raw uncoerced DOM value; resolvedProjectStatusId converts it on the way out. */
const projectStatusId = ref<string | number | null>(null);
const projectStatusOptions = BuildingProjectStageEnums.getAsArray().map((stage) => ({ name: stage.key, value: stage.value }));

const resolvedProjectStatusId = computed(() => {
  if (projectStatusId.value === null || projectStatusId.value === "") return null;
  const id = Number(projectStatusId.value);
  return Number.isNaN(id) ? null : id;
});

/** `models` is shared with the /create-project wizard's model step (NewSpeckleStream.vue); `immediate: true` so a country chosen before this watcher runs isn't missed. */
watch(
  () => defaultModels.getDefaultModels(),
  () => models.setDefaultModels(),
  { immediate: true }
);

const allModels = computed(() => models.getModels());

const toggleModel = (model: LocalModelType) => models.toggleModel(model);
const addCustomModel = (model: LocalModelType) => models.addModel(model);

/** Optional — the payload has always silently defaulted both years to today; pre-filled with that same default so leaving either untouched creates exactly the project it always did. */
const currentYear = new Date().getFullYear();
const yearOfConstruction = ref<number | null>(currentYear);
const yearOfCommissioning = ref<number | null>(currentYear);

/** The earliest year YearInput's own picker offers — matches its "Cannot be before 2023" copy. */
const EARLIEST_YEAR = 2023;

const yearOfConstructionError = computed(() =>
  yearOfConstruction.value !== null && yearOfConstruction.value < EARLIEST_YEAR ? "Cannot be before 2023" : undefined
);
const yearOfCommissioningError = computed(() =>
  yearOfCommissioning.value !== null && yearOfCommissioning.value < EARLIEST_YEAR ? "Cannot be before 2023" : undefined
);

// A cleared or invalid year falls back to the default rather than blocking Create — validated (same
// floor as ProjectDetails.vue) but not enforced as a fifth required answer.
const resolvedYearOfConstruction = computed(() =>
  yearOfConstruction.value !== null && yearOfConstruction.value >= EARLIEST_YEAR ? yearOfConstruction.value : currentYear
);
const resolvedYearOfCommissioning = computed(() =>
  yearOfCommissioning.value !== null && yearOfCommissioning.value >= EARLIEST_YEAR ? yearOfCommissioning.value : currentYear
);

/** Named in the loading message, with a stand-in only because an unnamed project cannot reach it. */
const creatingProjectName = computed(() => projectName.value.trim() || "your project");

/** No exempt name, unlike the settings page's check — every hit here belongs to another project. Input-driven only, no `checkImmediately`, since the field opens empty. */
const { nameExists: nameTaken, check: checkName } = useProjectNameAvailability();

const countryOptions = creatableCountries.map((entry) => ({ name: entry.longName, value: entry.country }));

// The select emits the raw DOM string, and Country_Enum's value IS a string, so this only rejects
// anything not in the options rather than converting it — unlike the numeric calculation type below.
const countrySelection = computed({
  get: () => selectedCountry.value,
  set: (value) => {
    selectedCountry.value = countryOptions.some((option) => option.value === value) ? (value as Country_Enum) : null;
  },
});

// Country-scoped selections (calc type, building type, models) are dropped when the country changes —
// keeping a stale id, or a model list, would create a project against options that country doesn't offer.
watch(selectedCountry, () => {
  calculationTypeId.value = null;
  models.setModels([]);
  buildingTypeId.value = null;
});

// The " - common choice" suffix is baked into the option string itself (SelectInputWithKeyValue renders
// plain <option> text) and matched on `label` — fragile to a Hasura rename of that field.
const calculationTypeOptions = computed(() => {
  const commonLabel = commonCalculationTypeLabelFor(selectedCountry.value);
  return calculationTypes.getCalculationTypes().map((calculationType) => ({
    // The suffix drops once selected: a native select shows the closed control's text at full width,
    // where it clipped to "BR18 (2025) - common…".
    name:
      calculationType.label === commonLabel && Number(calculationTypeId.value) !== calculationType.id
        ? `${calculationType.label} - common choice`
        : calculationType.label,
    value: calculationType.id,
  }));
});

/** Provider gives a flat list: `disabled` entries are pseudo-headings, U+00A0-prefixed labels are their children — rebuilt sequentially into real <optgroup>s here. */
type BuildingTypeItem =
  | { kind: "group"; label: string; options: Array<{ name: string; value: string }> }
  | { kind: "option"; name: string; value: string };

const buildingTypeItems = computed<BuildingTypeItem[]>(() => {
  const items: BuildingTypeItem[] = [];
  let openGroup: Extract<BuildingTypeItem, { kind: "group" }> | null = null;

  for (const buildingType of buildingTypes.getBuildingTypes()) {
    const isChild = isIndentedBuildingType(buildingType.label);
    const name = buildingTypeDisplayLabel(buildingType.label);

    if (buildingType.disabled) {
      openGroup = { kind: "group", label: name, options: [] };
      items.push(openGroup);
      continue;
    }

    if (isChild && openGroup) {
      openGroup.options.push({ name, value: buildingType.id });
      continue;
    }

    // A top-level, selectable type. It ends any open group, since nothing after it is that group's.
    openGroup = null;
    items.push({ kind: "option", name, value: buildingType.id });
  }

  // A heading with nothing under it would render as an empty group, so drop it.
  return items.filter((item) => item.kind === "option" || item.options.length > 0);
});

const selectedCalculationType = computed(() => {
  if (calculationTypeId.value === null || calculationTypeId.value === "") return undefined;

  // Number(): SelectInputWithKeyValue emits the raw DOM string; comparing that against numeric ids
  // silently matched nothing and left Continue disabled with a type visibly selected.
  const id = Number(calculationTypeId.value);
  if (Number.isNaN(id)) return undefined;

  return calculationTypes.getCalculationTypes().find((calculationType) => calculationType.id === id);
});

/** Detected by what the type ANSWERS, not by id or label: `id === 0` is only Denmark's Custom (Sweden's is 20), and label breaks on a rename. */
const customDefaults = computed(() =>
  selectedCalculationType.value ? calculationTypeDefaults(selectedCalculationType.value) : null
);

const needsCalculationDetails = computed(
  () =>
    customDefaults.value !== null &&
    (customDefaults.value.considerationPeriod === null || customDefaults.value.includedPhases.length === 0)
);

const customConsiderationPeriod = ref<number | null>(null);
const customThresholdLimit = ref<number | null>(null);
/** Stage keys as the strip and the store's `includedPhasesTranslated` carry them ("A1A3", "A4", …). */
const customStages = ref<string[]>([]);

// Cleared when the type changes so stale numbers aren't applied to a different type's choice — the
// country watcher above also fires this indirectly, since it nulls calculationTypeId.
watch(calculationTypeId, () => {
  customConsiderationPeriod.value = null;
  customThresholdLimit.value = null;
  customStages.value = [];
});

const toggleCustomStage = (value: string) => {
  customStages.value = customStages.value.includes(value)
    ? customStages.value.filter((stage) => stage !== value)
    : [...customStages.value, value];
};

// The same unit sentence CalculationTypeForm puts on this field, from the type's own threshold
// indicator. Guarded, unlike there: nothing promises a Custom row carries one.
const indicators = useIndicators();
const customThresholdLabel = computed(() => {
  const indicator = selectedCalculationType.value?.thresholdIndicator;
  if (!indicator) return "Limit value";
  return `Limit value (${indicators.getIndicatorUnit(
    indicator.indicatorType as IndicatorTypeType,
    indicator.denominatorUnit as DenominatorUnitType
  )})`;
});

/** Area 0 is safe, not a stand-in for a missing answer: `getThresholds` matches `totalArea <= maxTotalArea`, which holds at 0 — areas aren't asked in this dialog. */
const resolvedThresholdFor = (calculationType: SelectableCalculationType): number | undefined => {
  // Guarded: `getThresholds` calls `.find()` on the rows array without checking it exists, so a type
  // with none (Custom, Level(s), BR18 (2023)) would throw. Returning undefined leaves the flat `threshold` to stand.
  const rows = calculationType.thresholdByBuildingType;
  if (buildingTypeId.value === null || !Array.isArray(rows) || rows.length === 0) return undefined;

  return calculationTypes.getThresholds(
    calculationType as CalculationType,
    buildingTypeId.value,
    0,
    suppressThresholdFallback(selectedCountry.value, calculationType.label)
  ).threshold;
};

// Two of these four answers have a real id 0 (Kontor, Custom) — a truthiness check would drop a
// legitimate choice, so the rule lives in utils/canStartProject as null checks instead.
const canCreate = computed(() =>
  canStartProject({
    country: selectedCountry.value,
    projectName: projectName.value,
    buildingTypeId: buildingTypeId.value,
    hasCalculationType: !!selectedCalculationType.value,
    nameTaken: nameTaken.value,
    needsCalculationDetails: needsCalculationDetails.value,
    considerationPeriod: customConsiderationPeriod.value,
    thresholdLimit: customThresholdLimit.value,
    stageCount: customStages.value.length,
    // At least one, no maximum: a project needs a model to publish into.
    activeModelCount: allModels.value.filter((model) => model.isActive).length,
  })
);

// Matches the revision modals rather than the Button reusable: on the design-system-2.0 surfaces a
// token-styled native button IS the shared pattern, and Button carries the older FontAwesome look.
const actionClass =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";

// No way past these questions other than Cancel — a skip would leave country null, which every data
// provider reads as Denmark. Cancel also clears the shared models store used by the /create-project wizard.
const cancel = () => {
  models.setModels([]);
  closeByName("StartProjectModal", { action: "cancel" });
};

// The service returns the axios error it caught; the API's rejection message lives at
// response.data.message when the request reached it. Anything shaped differently gets the fallback.
const createErrorMessage = (error: unknown): string => {
  // The base project can exist with unwritten location/calculation rows; the dialog stays open and the
  // store keeps the id, so pressing Create again retries on the same project instead of a new one.
  if (error instanceof ProjectSetupIncompleteError) return error.message;
  if (isAxiosError(error)) {
    const message: unknown = error.response?.data?.message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "The project could not be created. Please try again.";
};

/** Seed the store from these four answers, create the project, and go to it. */
const create = async () => {
  // Tests for null rather than falsiness, for the same reason as canCreate: id 0 is selectable.
  if (isCreatingProject.value) return;
  if (!canCreate.value || !selectedCountry.value || !selectedCalculationType.value || buildingTypeId.value === null) return;

  const project = createBuildingProject.getProject();
  const { calculationType: assumptions } = project.emissionCalculationAssumptions;

  // The calculation type's phases feed the calculation row written beside the project.
  const defaults = calculationTypeDefaults(selectedCalculationType.value);
  const resolvedThreshold = resolvedThresholdFor(selectedCalculationType.value);

  createBuildingProject.setProject({
    ...project,
    projectName: projectName.value.trim(),
    buildingTypeEnum: buildingTypeId.value,
    // Optional: CreateBuildingProjectService reads this same field for every other caller of setProject
    // too, so leaving it null here is what "not answered" already means there.
    buildingProjectStageEnum: resolvedProjectStatusId.value,
    buildingAddress: {
      ...project.buildingAddress,
      // The only screen that writes the country. Settings can edit the address, but its search is scoped
      // to the country the project already has, so this answer is the one that settles it.
      country: countryLongName(selectedCountry.value) ?? project.buildingAddress.country,
    },
    emissionCalculationAssumptions: {
      ...project.emissionCalculationAssumptions,
      calculationType: {
        ...assumptions,
        calculationTypeEnum: defaults.calculationTypeEnum,
        // The type's own answer first, the dialog's Custom fields second — canStartProject guarantees
        // they are answered whenever the type answers nothing itself.
        considerationPeriod: defaults.considerationPeriod ?? customConsiderationPeriod.value ?? assumptions.considerationPeriod,
        // Resolved row first, flat threshold second — the order the Calculation card applied them in, where
        // updateThresholds() ran after the defaults and overwrote them for any type carrying rows.
        thresholdLimitWithoutExtra:
          resolvedThreshold ??
          defaults.thresholdLimitWithoutExtra ??
          customThresholdLimit.value ??
          assumptions.thresholdLimitWithoutExtra,
      },
      emissionMetaData: {
        ...project.emissionCalculationAssumptions.emissionMetaData,
        // Resolved rather than the raw refs, so a cleared or sub-2023 field still creates the project
        // instead of sending a value the backend's NOT NULL columns reject.
        yearOfConstruction: resolvedYearOfConstruction.value,
        yearOfCommissioning: resolvedYearOfCommissioning.value,
        includedPhases:
          defaults.includedPhases.length > 0
            ? defaults.includedPhases
            : BuildingProjectServices.includedPhasesConversion.getIncludedPhasesAsEnum(customStages.value),
        includedPhasesTranslated:
          defaults.includedPhasesTranslated.length > 0 ? defaults.includedPhasesTranslated : [...customStages.value],
      },
    },
  });

  isCreatingProject.value = true;
  try {
    // No stream, deliberately: a 3D model is attached to a project that exists, from the project's own
    // settings, and nothing here has asked about one.
    const response = await BuildingProjectServices.createBuildingProject({ speckleConnectionChoice: 0 });

    if (response !== 200) {
      Sentry.captureException(response);
      toast.error(createErrorMessage(response));
      return;
    }

    const newProjectId = createBuildingProject.getBuildingProjectId();
    if (!newProjectId) {
      // A 200 with no id is not a project anyone can be sent to, and silently closing would look like
      // nothing happened.
      Sentry.captureException(new Error("Project created but no id was returned"));
      toast.error("The project was created but could not be opened. Find it in your project list.");
      return;
    }

    /** Dashboard only while the project tour is pending — the tour itself ends on Settings. Otherwise straight to Settings. */
    const showsProjectTour = await isProjectTourPending();
    if (showsProjectTour) useWelcomeFlowStore().markNextProjectTourAfterCreation();
    await router.push(
      showsProjectTour
        ? { name: "projectBreakdown", params: { id: newProjectId } }
        : { name: "projectSettings", params: { id: newProjectId } }
    );

    // Reset here: left set, it would seed the next create with this project's answers.
    createBuildingProject.reset();
    // Same reasoning as cancel() above: this project's models were already sent, so the shared store
    // has nothing left to say for whatever gets created next.
    models.setModels([]);
    closeByName("StartProjectModal", { action: "created" });
  } finally {
    isCreatingProject.value = false;
  }
};
</script>

<template>
  <!-- Renderless, and reactive to the country chosen below: all three refetch whenever that changes. -->
  <CalculationTypes :country="selectedCountry" />
  <BuildingTypes :country="selectedCountry" />
  <DefaultModels :country="selectedCountry" />

  <div class="flex w-full flex-col gap-4">
    <!-- Full-body loading state: create is a few seconds of REST + Hasura writes, and the form behind it is
         done being edited. min-h guards against the parent's `min-h-0!` collapsing the panel. -->
    <div
      v-if="isCreatingProject"
      class="flex min-h-[260px] flex-col items-center justify-center gap-4">
      <!-- Not aria-hidden: the message below stays in the document for screen readers too, so nothing announces the project's name twice. -->
      <LoadingLogoSpinner />
      <div class="flex flex-col items-center gap-1">
        <p class="t-sh4 text-type-default">Creating {{ creatingProjectName }}</p>
        <p class="t-caption text-type-muted">Setting things up. This takes a few seconds.</p>
      </div>
    </div>

    <template v-else>
      <!-- h2, not h1: inside a dialog on the projects route, h1 would be a second page heading. -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Building2
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <h2 class="t-sh3 text-type-default">Start a new project</h2>
        </div>

        <!-- Settings can correct the address but restricts its search to the country chosen here, so changing the country means starting again. -->
        <p class="t-caption text-type-muted">
          The country is fixed for the life of the project, so check it now. Everything else — areas, energy, a 3D model, who can
          see it — you fill in on the project's settings page afterwards.
        </p>
      </div>

      <!-- Country has its own row: changing it clears the country-scoped selects below, so it should not read as one of a pair. DOM order matches the visual order for tab order. -->
      <div class="grid grid-cols-12 gap-x-4 gap-y-4">
        <SelectInputWithKeyValue
          v-model:value="countrySelection"
          class="col-span-12"
          :options="countryOptions"
          placeholder="Select country"
          label="Country"
          tooltip="Where the building is. The country decides which calculation types, emission data and building types are available, and it cannot be changed later."
          :required="true" />

        <!-- Trimmed on the way in: `create` submits the trimmed name and the API compares with `_eq`, so the check must test the string that will actually be created. -->
        <TextInputNew
          v-model="projectName"
          class="col-span-12 sm:col-span-8"
          label="Project name"
          placeholder="What is this project called?"
          :error-msg="nameTaken ? PROJECT_NAME_TAKEN_MESSAGE : undefined"
          :maxlength="55"
          :remove-leading-whitespace="true"
          :required="true"
          @update:model-value="(value: string) => checkName(value.trim())" />

        <!-- Optional, unlike the name beside it — the base-project payload accepts it but needs it from nobody. -->
        <SelectInputWithKeyValue
          v-model:value="projectStatusId"
          class="col-span-12 sm:col-span-4"
          :options="projectStatusOptions"
          placeholder="Select project status"
          label="Project status"
          tooltip="Where the project stands right now. Optional — it can be set or changed from the project's settings afterwards." />

        <!-- SelectInputNew, not SelectInputWithKeyValue: it renders slotted children, needed for real <optgroup> markup. -->
        <SelectInputNew
          v-model="buildingTypeId"
          class="col-span-12 sm:col-span-6"
          cast="number"
          :disabled="!selectedCountry || buildingTypes.getLoading()"
          :placeholder="
            !selectedCountry ? 'Choose a country first' : buildingTypes.getLoading() ? 'Loading…' : 'Select building type'
          "
          label="Building type"
          tooltip="The primary function of the building (e.g. residential, office, school). It sets the limit value the project is measured against, together with the building's area."
          :required="true">
          <template
            v-for="item in buildingTypeItems"
            :key="item.kind === 'group' ? `group:${item.label}` : item.value">
            <optgroup
              v-if="item.kind === 'group'"
              :label="item.label">
              <option
                v-for="option in item.options"
                :key="option.value"
                :value="option.value">
                {{ option.name }}
              </option>
            </optgroup>
            <option
              v-else
              :value="item.value">
              {{ item.name }}
            </option>
          </template>
        </SelectInputNew>

        <!-- The base-project payload has always carried both years, silently defaulted to the current one — these let someone answer instead, without making either required. -->
        <YearInput
          v-model:value="yearOfConstruction"
          class="col-span-12 sm:col-span-3"
          color="primary"
          label="Year of construction"
          tooltip="The year construction of the building begins. Left as the current year unless changed."
          :error-msg="yearOfConstructionError"
          @new-selected="(year: number) => (yearOfConstruction = year)" />
        <YearInput
          v-model:value="yearOfCommissioning"
          class="col-span-12 sm:col-span-3"
          color="primary"
          label="Year of commissioning"
          tooltip="The year the building is put into use. Left as the current year unless changed."
          :error-msg="yearOfCommissioningError"
          @new-selected="(year: number) => (yearOfCommissioning = year)" />

        <!-- Its own row, directly above the models it decides so much about. Disabled until a country is
             chosen, because the options on offer are that country's. -->
        <SelectInputWithKeyValue
          v-model:value="calculationTypeId"
          class="col-span-12"
          :options="calculationTypeOptions"
          :is-disabled="!selectedCountry || calculationTypes.getLoading()"
          :placeholder="
            !selectedCountry ? 'Choose a country first' : calculationTypes.getLoading() ? 'Loading…' : 'Select calculation type'
          "
          label="Calculation type"
          tooltip="The assessment method the project is measured against. It sets which life-cycle stages are included."
          :required="true" />

        <!-- Shown only for a calculation type that answers nothing itself (Custom); Create stays disabled until all three are given — see canStartProject. -->
        <template v-if="needsCalculationDetails">
          <NumberInput
            v-model:value="customConsiderationPeriod"
            class="col-span-12 sm:col-span-6"
            color="primary"
            label="Consideration period (years)"
            tooltip="The length of time, in years, over which the building’s life cycle impacts are assessed. This period defines how long operational impacts and replacements are included in the calculation."
            :positive="true"
            :required="true" />
          <NumberInput
            v-model:value="customThresholdLimit"
            class="col-span-12 sm:col-span-6"
            color="primary"
            :label="customThresholdLabel"
            tooltip="The limit value used to evaluate the project’s climate performance. This allows results to be compared against regulations, certification requirements, or project-specific goals."
            :positive="true"
            :required="true" />
          <div class="col-span-12 flex flex-col gap-2">
            <span class="t-caption text-type-muted">Life-cycle stages</span>
            <LifecycleStageStrip
              :included-stages="customStages"
              :editable="true"
              @toggle-stage="toggleCustomStage" />
            <p
              v-if="customStages.length === 0"
              class="t-caption text-type-muted">
              Select at least one stage to include in the calculation.
            </p>
          </div>
        </template>

        <!-- Last row, so its panel opens upward — growing downward would extend past the dialog. -->
        <ModelSelect
          class="col-span-12"
          :models="allModels"
          label="Models"
          required
          placement="top-start"
          placeholder="Select at least one model"
          caption="Tick the models this project will hold, or create your own below."
          @toggle-model="toggleModel"
          @set-all="models.setAllActive"
          @add-model="addCustomModel" />
      </div>

      <div class="flex items-center justify-end gap-2 pt-1">
        <!-- The ghost pattern's ring, not a border: ring-slate-250 is the token cut for exactly this —
             a quiet outline on a bg-slate-0 button (theme.md, Neutral slate). -->
        <button
          type="button"
          :class="[actionClass, 'bg-slate-0 text-type-default ring-slate-250 ring-1 hover:bg-slate-100 hover:ring-slate-300']"
          data-cy="start-project-cancel"
          @click="cancel">
          Cancel
        </button>
        <button
          type="button"
          :disabled="!canCreate"
          :class="[
            actionClass,
            canCreate
              ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
              : 'text-type-muted bg-slate-200',
            { 'interactive-disabled': !canCreate },
          ]"
          data-cy="start-project"
          @click="create">
          Create project
          <ArrowRight
            class="icon-sm"
            :stroke-width="1.5" />
        </button>
      </div>
    </template>
  </div>
</template>
